<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useContent } from '@/composables/useContent'
import { useContentLinks } from '@/composables/useContentLinks'
import { useEditAffordance } from '@/composables/useEditAffordance'
import { expandIcons } from '@/content/expandIcons'
import ContentInlineEditor from '@/components/ContentInlineEditor.vue'

// An inline, runtime-editable content region. Renders the stored block (`name`) when one exists and is
// public; otherwise renders the DEFAULT SLOT (the current hardcoded HTML) — so wrapping existing markup
// is non-breaking and the page works before any block is created. Admins in edit mode get a ✎ to edit
// the region in place; the first save creates the block (publicView=true).
//
// `seed` (optional): the editor's starting HTML for a not-yet-created block, used for glyph-bearing
// sections whose coded default contains raw <svg> (which the sanitizer would strip). Provide the
// token-based equivalent (e.g. "...[[icon:arrow-right]]") so the first save round-trips cleanly. When
// omitted, the editor seeds from the slot's own HTML (fine for pure-prose sections).
//
// Naming convention for `name`: kebab-case `{page}-{section}` (e.g. home-intro, footer-copyright),
// distinct from the whole-page content keys used by ContentRegion.
const props = defineProps<{ name: string; seed?: string }>()

const content = useContent()
const entry = content.request(props.name)
const { onContentClick } = useContentLinks()

const { showAffordance } = useEditAffordance()

const showDb = computed(() => entry.state.value === 'ready')
const renderedHtml = computed(() => expandIcons(entry.html.value))

// Snapshot the coded default HTML once (before any swap to DB content) to seed the editor when no
// `seed` prop is given and no block exists yet.
const slotEl = ref<HTMLElement | null>(null)
const defaultHtml = ref('')
onMounted(() => {
  if (slotEl.value) defaultHtml.value = slotEl.value.innerHTML
})
const editorSeed = computed(() => props.seed ?? defaultHtml.value)

const editing = ref(false)
function openEditor() {
  editing.value = true
}
function onSaved() {
  editing.value = false
  content.refresh(props.name)
}
</script>

<template>
  <div class="content-text" :class="{ 'ct-editable': showAffordance }">
    <button
      v-if="showAffordance"
      type="button"
      class="ct-pencil"
      title="Edit this content"
      aria-label="Edit this content"
      @click="openEditor"
    >&#9998;</button>

    <div v-if="showDb" class="ct-body" v-html="renderedHtml" @click="onContentClick"></div>
    <div v-else ref="slotEl" class="ct-body"><slot /></div>

    <ContentInlineEditor
      v-if="editing"
      :name="name"
      :default-html="editorSeed"
      @saved="onSaved"
      @cancel="editing = false"
    />
  </div>
</template>

<style scoped>
/* Transparent wrapper by default — must not disturb the surrounding layout. */
.content-text { display: contents; }
/* In edit mode the wrapper becomes a positioned box so the ✎ can anchor + an outline shows the region. */
.content-text.ct-editable {
  display: block;
  position: relative;
  outline: 1px dashed var(--accent, #1f6fd4);
  outline-offset: 2px;
}
.ct-pencil {
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 5;
  width: 26px;
  height: 26px;
  border-radius: 0;
  border: 1px solid var(--ink, #0d151d);
  background: var(--paper, #f5f8fb);
  color: var(--accent, #1f6fd4);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(13, 21, 29, 0.25);
}
.ct-pencil:hover {
  background: var(--accent, #1f6fd4);
  border-color: var(--accent, #1f6fd4);
  color: #fff;
}
.ct-body { display: contents; }
.content-text.ct-editable .ct-body { display: block; }
</style>
